import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckCheck } from "lucide-react";
import api from "@/api";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

export const NotificationBell = () => {
  const { t } = useLanguage();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/notifications");
      setItems(data.items);
      setUnread(data.unread);
    } catch { /* sessão expirada ou offline — ignorar */ }
  }, []);

  useEffect(() => {
    setItems([]);
    setUnread(0);
    load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [load, user?.id]);

  const openItem = (n) => {
    if (!n.read) {
      api.post(`/notifications/${n.id}/read`).catch(() => {});
      setUnread((u) => Math.max(0, u - 1));
      setItems((list) => list.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
    }
    const path = (n.link || "").replace(/^https?:\/\/[^/]+/, "") || "/painel";
    navigate(path);
  };

  const markAll = () => {
    api.post("/notifications/read-all").catch(() => {});
    setUnread(0);
    setItems((list) => list.map((x) => ({ ...x, read: true })));
  };

  const text = (n) => t(`notifs.${n.event}`).replace(/\{(\w+)\}/g, (_, k) => n.params?.[k] ?? "");

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid="notif-bell" className="relative rounded-full p-2 text-slate-600 hover:bg-slate-100 hover:text-primary transition-colors duration-200">
          <Bell size={18} />
          {unread > 0 && (
            <span data-testid="notif-badge" className="absolute -top-0.5 -right-0.5 bg-accent text-white text-[10px] font-bold min-w-4 h-4 px-1 rounded-full flex items-center justify-center">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-80 max-h-96 overflow-y-auto p-0 rounded-2xl" data-testid="notif-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <span className="font-heading font-semibold text-sm text-primary" data-testid="notif-title">{t("notifs.title")}</span>
          {unread > 0 && (
            <button data-testid="notif-mark-all" onClick={markAll} className="text-xs text-accent hover:underline flex items-center gap-1">
              <CheckCheck size={13} />{t("notifs.markAll")}
            </button>
          )}
        </div>
        {items.length === 0 ? (
          <p className="text-sm text-slate-400 px-4 py-6 text-center" data-testid="notif-empty">{t("notifs.empty")}</p>
        ) : (
          items.map((n) => (
            <button
              key={n.id}
              data-testid={`notif-item-${n.id}`}
              onClick={() => openItem(n)}
              className={`w-full text-left px-4 py-3 text-sm border-b border-slate-50 hover:bg-slate-50 transition-colors duration-150 ${n.read ? "text-slate-500" : "text-slate-800 font-medium bg-accent/5"}`}
            >
              {text(n)}
              <span className="block text-xs text-slate-400 mt-0.5">{(n.created_at || "").slice(0, 16).replace("T", " ")}</span>
            </button>
          ))
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
